/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
export const HOME = "/"

export const LOGIN = "/login"
export const LOGIN_SUCCESS = "/login-success"

export const LOGOUT = "/profile/logout"

export const OUTLET = (storeID?: StoreID): string => {
  if (storeID) {
    return `/food/stores/${storeID}`
  }
  return `/food/stores/:storeID`
}

export const OUTLET_ORDERS = (
  storeID = ":storeID",
  longOrderID = ":longOrderID",
): string => `/food/stores/${storeID}/orders/${longOrderID}`

export const OUTLET_TRANSACTIONS = (
  storeID = ":storeID",
  transactionID = ":txnID",
): string => `/food/stores/${storeID}/transactions/${transactionID}`

export const OUTLET_OVO_TRANSACTIONS = (
  storeID = ":storeID",
  transactionID = ":txnID",
): string => `/food/stores/${storeID}/ovoTransactions/${transactionID}`

export const OUTLET_BOOKING_ID_PATTERN = new RegExp("bookingID=([\\w-]+)")

export const OUTLET_PATTERN = new RegExp("/food/stores/?")

export const OUTLET_ORDERS_PATTERN = new RegExp(
  "/food/stores/([\\w-]+)/orders/([\\w-]+)",
)
export const OUTLET_TRANSACTIONS_PATTERN = new RegExp(
  "/food/stores/([\\w-]+)/transactions/([\\w-]+)",
)

export const OUTLET_OVO_TRANSACTIONS_PATTERN = new RegExp(
  `/food/stores/([\\w-]+)/ovoTransactions/([\\w-]+)`,
)

export const CHAIN = (): string => {
  return `/food/merchant`
}

export const CHAIN_PATTERN = new RegExp("/food/merchant/?")

export const MME_URL_PREFIX = `/food`

export const MENUS = `${MME_URL_PREFIX}/menu`

export const MENU_DETAIL_PATTERN = new RegExp(`${MENUS}/(.*\\S.*)`)

export const CATALOGUES = `${MME_URL_PREFIX}/catalogue`

export const CATALOGUE_DETAIL_PATTERN = new RegExp(`${CATALOGUES}/(.*\\S.*)`)

export const CREATE_MENU = `${MME_URL_PREFIX}/create-menu`
export const CREATE_CATALOGUE = `${MME_URL_PREFIX}/create-catalogue`
export const INVENTORY = `${MME_URL_PREFIX}/inventory`

export const LOCATIONS = `/locations`

export const FEEDBACK = `/feedback`

export const ECOMMERCE_URL_PREFIX = `/shop`

export const MART_ECOMM_MIGRATION_PREVIEW = `/mart-ecomm-migration-preview`

export const MART_ECOMM_CATALOG_MIGRATION = `/mart-ecomm-catalog-migration`
export const MART_ECOMM_CATALOG_MIGRATION_REVIEW = `${MART_ECOMM_CATALOG_MIGRATION}/review`

export const ECOMMERCE_PRODUCTS = (storeID = ":storeID"): string =>
  `${ECOMMERCE_URL_PREFIX}/${storeID}/products`

export const ECOMMERCE_PRODUCTS_LIST = (storeID = ":storeID"): string =>
  `${ECOMMERCE_URL_PREFIX}/${storeID}/products/list`

export const ECOMMERCE_PRODUCTS_ADD = (storeID = ":storeID"): string =>
  `${ECOMMERCE_URL_PREFIX}/${storeID}/products/add`

export const ECOMMERCE_PRODUCTS_BULK_ADD = (storeID = ":storeID"): string =>
  `${ECOMMERCE_URL_PREFIX}/${storeID}/products/bulk-add`

export const ECOMMERCE_PRODUCTS_BULK_UPDATE_STOCK_COUNT = (
  storeID = ":storeID",
): string =>
  `${ECOMMERCE_URL_PREFIX}/${storeID}/products/bulk-update-stock-count`

export const ECOMMERCE_PRODUCTS_EDIT = (
  storeID = ":storeID",
  productID = ":productID",
): string => `${ECOMMERCE_URL_PREFIX}/${storeID}/products/edit/${productID}`

export const ECOMMERCE_PRODUCTS_DETAIL = (
  storeID = ":storeID",
  productID = ":productID",
): string => `${ECOMMERCE_URL_PREFIX}/${storeID}/products/${productID}`

export const STOREFRONT_COLLECTION_URL_PREFIX = "/storefront-collection"

// Matches an exact prefix or a path segment under it, so "/shop" and "/shop/123"
// match but sibling paths like "/shopping" do NOT.
const isUnderPrefix = (pathname: string, prefix: string): boolean =>
  pathname === prefix || pathname.startsWith(`${prefix}/`)

const isCatalogMigrationIntroPath = (pathname: string): boolean =>
  pathname === MART_ECOMM_CATALOG_MIGRATION ||
  pathname === `${MART_ECOMM_CATALOG_MIGRATION}/`

const isCatalogMigrationPath = (pathname: string): boolean =>
  isCatalogMigrationIntroPath(pathname) ||
  pathname === MART_ECOMM_CATALOG_MIGRATION_REVIEW ||
  pathname === `${MART_ECOMM_CATALOG_MIGRATION_REVIEW}/`

// True when a pathname is an eCom page (products, collections or migration). Used to
// toggle the `ecom-page` body class so global CSS releases the desktop content-width
// floor for eCom pages — route-scoped, NOT webview-gated (see utils/webview.ts).
export const isEcomPath = (pathname: string): boolean =>
  isCatalogMigrationPath(pathname) ||
  isUnderPrefix(pathname, ECOMMERCE_URL_PREFIX) ||
  isUnderPrefix(pathname, STOREFRONT_COLLECTION_URL_PREFIX) ||
  // The return request page lives under the order-manager prefix (a return is an order
  // concern) but is an eCom surface the app opens at ~390px, so it needs the floor
  // released too — matched by pattern rather than prefix. Defined below with the route.
  ORDER_RETURN_PATH.test(pathname)

// Product add/edit — the stepped wizard. Its header band, progress bar and sticky
// action bar run edge to edge, which needs the shell's centred content-width cap
// released (see `body.ecom-full-bleed` in styles/global.ts). Deliberately narrower
// than `isEcomPath`: pages like the products list still rely on that cap to stop
// their table stretching across an ultra-wide screen.
// Also the mart catalog-migration intro: its teal band runs edge to edge beside
// the sidebar, while the page keeps its own 1040px cap on the readable layout.
const ECOM_FULL_BLEED_PATH = new RegExp(
  `^${ECOMMERCE_URL_PREFIX}/[^/]+/products/(add|edit)(/|$)`,
)

export const isEcomFullBleedPath = (pathname: string): boolean =>
  isCatalogMigrationIntroPath(pathname) || ECOM_FULL_BLEED_PATH.test(pathname)

// Everything below `/shop/:storeID/products/`, anchored at BOTH ends with an
// optional trailing slash. Anchoring the tail is what keeps the fail-open
// promise honest: without it `/products/SKU-1/unknown` would be classified off
// its first segment and hide the bar, even though no route matches it and the
// merchant is looking at a 404.
const ECOM_PRODUCTS_SUBPATH = new RegExp(
  `^${ECOMMERCE_URL_PREFIX}/[^/]+/products/(.+?)/?$`,
)

// Single leaf segments under `/products/` that are NOT a product ID, so they
// keep the bar. Every other single segment is read as a product ID.
const PRODUCTS_SEGMENTS_KEEPING_NAV = ["list"]

// The only full-page product route that is two segments deep
// (`/products/edit/:productID`); everything else is one.
const PRODUCTS_EDIT_SEGMENT = "edit"

/**
 * The app's bottom nav bar: true when the current route is a FULL-PAGE eCom
 * screen that must render without it.
 *
 * `/shop/:storeID/products/list` is the one page hosted *inside* the app's tab,
 * so it keeps the bar. Everything deeper under `/products/` is a full-page
 * screen (detail, add/edit wizard, bulk flows) and drops it.
 *
 * **Fails open — an unrecognised pathname keeps the bar.** A wrongly-visible bar
 * is cosmetic and the merchant can still navigate away; a wrongly-hidden one
 * traps them in a webview with no bottom nav. So this answers "hide?", not
 * "show?", and every non-eCom route, the `/shop` and `/shop/:storeID/products`
 * normalization hops, and anything unknown fall through to `false`.
 *
 * NOTE the one place that bias cannot hold: product detail is
 * `/products/<productID>`, structurally identical to any future sibling page in
 * that position, so a single unknown segment is read as a product ID and hides
 * the bar. A new page added there must be listed in
 * `PRODUCTS_SEGMENTS_KEEPING_NAV` or it will render without the bar.
 */
export const isEcomBottomNavHiddenPath = (pathname: string): boolean => {
  // Not a product sub-page at all: /shop, /shop/:storeID/products, storefront
  // collections, every non-eCom route in the portal.
  const subPath = ECOM_PRODUCTS_SUBPATH.exec(pathname)?.[1]
  if (!subPath) return false

  const segments = subPath.split("/")
  // One segment: a product ID unless it is a page named above. Two: only the
  // edit wizard. Anything else — a deeper path, or a malformed one like
  // `/products//foo` whose first segment is empty — matches no route, so the
  // merchant is on a 404 and keeps the bar.
  if (segments.length === 1) {
    return !PRODUCTS_SEGMENTS_KEEPING_NAV.includes(segments[0])
  }
  if (segments.length === 2) return segments[0] === PRODUCTS_EDIT_SEGMENT
  return false
}

export const STOREFRONT_COLLECTION_LIST = (storeID = ":storeID"): string =>
  `${STOREFRONT_COLLECTION_URL_PREFIX}/${storeID}/list`

export const STOREFRONT_COLLECTIONS_ADD = (storeID = ":storeID"): string =>
  `${STOREFRONT_COLLECTION_URL_PREFIX}/${storeID}/add`

export const STOREFRONT_COLLECTIONS_EDIT = (
  storeID = ":storeID",
  collectionID = ":collectionID",
): string =>
  `${STOREFRONT_COLLECTION_URL_PREFIX}/${storeID}/edit/${collectionID}`

interface MenuDetailParams {
  isMartStore?: boolean
  merchantID?: string
  tabKey?: string
  itemID?: string
  customRoutePrefix?: string
}

export const MENU_DETAIL = ({
  isMartStore = false,
  merchantID = ":merchantID",
  tabKey = ":tabKey",
  itemID = ":itemID",
  customRoutePrefix,
}: MenuDetailParams): string => {
  let prefix = MENUS
  if (isMartStore) prefix = CATALOGUES
  if (customRoutePrefix) prefix = customRoutePrefix
  return `${prefix}/${merchantID}/${tabKey}/${itemID}`
}

// ~ORDER MANAGER~ <-- use this pattern to find order manager related code outside it's main directory (for code migration purpose)
export const ORDER_MANAGER_URL_PREFIX = "/order"

/**
 * A single return request, rendered without the list around it — the URL the native app opens
 * in its webview. Lives under the order-manager prefix because a return request is an order
 * concern, and the merchant reaches it from the Orders tab.
 *
 * Being under `/order` also makes `isInOrderPage()` true, which skips the blocking store-list
 * fetch that would otherwise run before anything paints.
 *
 * Keyed on the ORDER, matching the review-detail endpoint: the MVP contract allows one
 * submitted request per delivered order, so the order fully identifies the request — and it
 * is the one identifier the opener (the native app's order/request list) always has. The
 * `requestID` arrives with the payload; keying the URL on it would force every opener to
 * carry a second identifier that nothing here can fetch by.
 *
 * No `?webview=true` needed — `isWebview()` resolves from the User-Agent. That param is only a
 * manual override for exercising the layout in a desktop browser.
 *
 * NOTE: the order list's `/order/:merchantID/:tabKey?/:orderID?` pattern also matches this URL,
 * so the route MUST be registered above the list in the Switch — see NavigationContainer.
 */
export const ORDER_RETURN_DETAIL = (
  merchantID = ":merchantID",
  orderID = ":orderID",
): string => `${ORDER_MANAGER_URL_PREFIX}/${merchantID}/returns/${orderID}`

/**
 * The per-item review step as its own URL, so the native back button steps from review back to
 * the request instead of closing the webview — a render-switch view is invisible to the
 * platform. Also makes the step deep-linkable.
 *
 * Four segments deep, so unlike the detail URL it does not collide with the list's pattern.
 */
export const ORDER_RETURN_REVIEW = (
  merchantID = ":merchantID",
  orderID = ":orderID",
): string => `${ORDER_RETURN_DETAIL(merchantID, orderID)}/review`

/** Decision 2's step URL — same rationale as the review step above. */
export const ORDER_RETURN_INSPECTION = (
  merchantID = ":merchantID",
  orderID = ":orderID",
): string => `${ORDER_RETURN_DETAIL(merchantID, orderID)}/inspect`

/**
 * Matches the return request page and its review step, for the `isEcomPath` content-width
 * release above. Without it the shell keeps its 900px floor and the page scrolls
 * horizontally in a 390px webview.
 */
const ORDER_RETURN_PATH = new RegExp(
  `^${ORDER_MANAGER_URL_PREFIX}/[^/]+/returns/[^/]+(/review|/inspect)?/?$`,
)

// ~ORDER MANAGER~ <-- use this pattern to find order manager related code outside it's main directory (for code migration purpose)
export const ORDER_MANAGER_URL_PATTERN = new RegExp(
  `${ORDER_MANAGER_URL_PREFIX}/(.*\\S.*)`,
)

const LOGIN_REDIRECT_URL = encodeURIComponent(
  `https://${window.location.host}${LOGIN_SUCCESS}`,
)

/**
 * Used for local development and web-kit branch previews.
 */
export const STG_WEB_LOGIN_URL = `https://weblogin.stg-myteksi.com/merchant/login?service_id=MEXUSERS&ui_locales=en&redirect=${LOGIN_REDIRECT_URL}`
